/**
 * Reusable Carousel Class
 * Handles common carousel functionality including touch/swipe, navigation buttons, progress bar, and pagination
 */
class Carousel {
  constructor(options = {}) {
    // Required elements
    this.container = options.container || null;
    // Convert NodeList or array-like to array
    this.slides = options.slides ? Array.from(options.slides) : [];
    
    // Optional elements
    this.prevBtn = options.prevBtn || null;
    this.nextBtn = options.nextBtn || null;
    this.progressBar = options.progressBar || null;
    this.paginationContainer = options.paginationContainer || null;
    this.slideCounter = options.slideCounter || null;
    
    // Configuration
    this.currentIndex = options.initialIndex || 0;
    this.gap = options.gap || 17; // Gap between slides in pixels
    this.breakpoint = options.breakpoint || 991; // Breakpoint for mobile/desktop
    this.swipeThreshold = options.swipeThreshold || 50; // Minimum swipe distance
    this.transitionDuration = options.transitionDuration || '0.5s ease';
    
    // Transform calculation options
    this.transformType = options.transformType || 'percentage'; // 'percentage' or 'pixels'
    this.slidePercentage = options.slidePercentage || 75; // Percentage of slide width for mobile
    
    // Touch/swipe state
    this.startX = 0;
    this.startY = 0;
    this.currentX = 0;
    this.isDragging = false;
    this.isHorizontalSwipe = null;
    
    // Callbacks
    this.onSlideChange = options.onSlideChange || null;
    this.updateTransform = options.updateTransform || null;
    
    // Conditional pagination: if true, pagination only shows on mobile (handled by initializeConditionalPagination)
    this.conditionalPagination = options.conditionalPagination || false;
    
    // Bind methods to preserve 'this' context
    this.boundHandleResize = null;
    
    // Initialize
    if (this.container && this.slides.length > 0) {
      this.init();
    }
  }
  
  init() {
    if (!this.container || !this.slides || this.slides.length === 0) {
      console.warn('Carousel: Missing required elements (container or slides)');
      return;
    }
    
    this.totalSlides = this.slides.length;
    
    // Setup navigation buttons
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prev());
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next());
    }
    
    // Setup pagination dots (skip if conditional pagination is enabled)
    if (this.paginationContainer && !this.conditionalPagination) {
      this.createPagination();
      // Ensure first dot is active on initialization
      // Force update pagination after a small delay to ensure DOM is ready
      setTimeout(() => {
        this.updatePagination();
      }, 0);
    }
    
    // Setup touch/swipe events
    this.setupTouchEvents();
    
    // Setup resize handler - bind correctly
    this.boundHandleResize = () => this.handleResize();
    window.addEventListener('resize', this.boundHandleResize);
    
    // Initial update
    this.update();
    
    // Initial check for mobile
    if (window.innerWidth <= this.breakpoint) {
      this.updateCarousel();
    } else {
      this.resetTransform();
    }
    
    // Final check: ensure first dot is active after all initialization
    // This handles cases where pagination might need to be synced
    if (this.paginationContainer && this.currentIndex === 0) {
      setTimeout(() => {
        this.updatePagination();
      }, 10);
    }
  }
  
  /**
   * Update carousel state
   */
  update() {
    if (!this.container || !this.slides || this.slides.length === 0) {
      return;
    }
    
    this.updateCarousel();
    this.updateProgressBar();
    this.updateButtonState();
    this.updatePagination();
    this.updateSlideCounter();
    
    // Call custom callback if provided
    if (this.onSlideChange) {
      this.onSlideChange(this.currentIndex);
    }
  }
  
  /**
   * Update carousel transform
   */
  updateCarousel() {
    if (!this.container) {
      return;
    }
    
    // Only apply transform on mobile (below breakpoint)
    if (window.innerWidth > this.breakpoint) {
      this.resetTransform();
      return;
    }
    
    let transform = '';
    
    try {
      if (this.updateTransform && typeof this.updateTransform === 'function') {
        // Use custom transform function
        transform = this.updateTransform(this.currentIndex, this);
      } else if (this.transformType === 'percentage') {
        // Percentage-based transform
        transform = `translateX(-${this.currentIndex * this.slidePercentage}%)`;
      } else {
        // Pixel-based transform with gap
        const slideWidth = this.slides[0]?.offsetWidth || 0;
        const slideDistance = this.currentIndex * (slideWidth + this.gap);
        transform = `translateX(-${slideDistance}px)`;
      }
      
      if (transform) {
        this.container.style.transition = this.transitionDuration;
        this.container.style.transform = transform;
      }
    } catch (error) {
      console.error('Carousel: Error updating carousel transform', error);
    }
  }
  
  /**
   * Reset transform (for desktop)
   */
  resetTransform() {
    if (this.container) {
      this.container.style.transform = 'translateX(0)';
    }
  }
  
  /**
   * Update progress bar
   */
  updateProgressBar() {
    if (!this.progressBar || this.totalSlides === 0) return;
    
    const progressPercentage = ((this.currentIndex + 1) / this.totalSlides) * 100;
    this.progressBar.style.width = `${progressPercentage}%`;
  }
  
  /**
   * Update button states (disabled/enabled)
   */
  updateButtonState() {
    if (this.prevBtn) {
      if (this.currentIndex === 0) {
        this.prevBtn.classList.add('disabled');
      } else {
        this.prevBtn.classList.remove('disabled');
      }
    }
    
    if (this.nextBtn) {
      if (this.currentIndex === this.totalSlides - 1) {
        this.nextBtn.classList.add('disabled');
      } else {
        this.nextBtn.classList.remove('disabled');
      }
    }
  }
  
  /**
   * Create pagination dots
   */
  createPagination() {
    if (!this.paginationContainer) return;
    
    this.paginationContainer.innerHTML = '';
    
    for (let i = 0; i < this.totalSlides; i++) {
      const button = document.createElement('button');
      // Set first button (index 0) as active by default
      if (i === 0 || i === this.currentIndex) {
        button.classList.add('active');
      }
      button.addEventListener('click', () => this.goTo(i));
      this.paginationContainer.appendChild(button);
    }
    
    // After creating all buttons, ensure the active state matches currentIndex
    // This ensures the first dot (index 0) is active by default on initial load
    this.updatePagination();
  }
  
  /**
   * Update pagination dots
   */
  updatePagination() {
    if (!this.paginationContainer) return;
    
    const buttons = this.paginationContainer.querySelectorAll('button');
    
    // If buttons don't exist yet, don't try to update (pagination will be created later)
    if (buttons.length === 0) {
      return;
    }
    
    // Remove active class from all buttons first
    buttons.forEach((button) => {
      button.classList.remove('active');
    });
    
    // Add active class to the current index button
    // This ensures only one button is active at a time
    // Default to first button (index 0) if currentIndex is out of range
    const targetIndex = (this.currentIndex >= 0 && this.currentIndex < buttons.length) 
      ? this.currentIndex 
      : 0;
    
    // Ensure we have a valid button before adding active class
    if (buttons[targetIndex]) {
      buttons[targetIndex].classList.add('active');
    } else if (buttons[0]) {
      // Fallback: always activate first button if targetIndex button doesn't exist
      buttons[0].classList.add('active');
    }
  }
  
  /**
   * Update slide counter
   */
  updateSlideCounter() {
    if (!this.slideCounter) return;
    
    if (this.totalSlides > 0) {
      this.slideCounter.textContent = `${this.currentIndex + 1}/${this.totalSlides}`;
    }
  }
  
  /**
   * Go to specific slide
   */
  goTo(index) {
    this.currentIndex = Math.max(0, Math.min(index, this.totalSlides - 1));
    this.update();
  }
  
  /**
   * Go to next slide
   */
  next() {
    if (this.currentIndex < this.totalSlides - 1) {
      this.currentIndex++;
      this.update();
    }
  }
  
  /**
   * Go to previous slide
   */
  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.update();
    }
  }
  
  /**
   * Setup touch/swipe events
   */
  setupTouchEvents() {
    if (!this.container) return;
    
    this.container.addEventListener('touchstart', (e) => {
      this.startX = e.touches[0].clientX;
      this.startY = e.touches[0].clientY;
      this.isDragging = true;
      this.isHorizontalSwipe = null;
      this.container.style.transition = 'none';
    }, { passive: true });
    
    this.container.addEventListener('touchmove', (e) => {
      if (!this.isDragging) return;
      
      const moveX = e.touches[0].clientX;
      const moveY = e.touches[0].clientY;
      const diffX = Math.abs(this.startX - moveX);
      const diffY = Math.abs(this.startY - moveY);
      
      // Determine swipe direction with threshold
      if (this.isHorizontalSwipe === null && (diffX > 15 || diffY > 15)) {
        this.isHorizontalSwipe = diffX > diffY;
      }
      
      // If vertical scroll, allow default behavior
      if (this.isHorizontalSwipe === false) {
        this.isDragging = false;
        return;
      }
      
      // If horizontal swipe, prevent default and handle swipe
      if (this.isHorizontalSwipe === true) {
        e.preventDefault();
        
        const difference = this.startX - moveX;
        
        if (difference > this.swipeThreshold && this.currentIndex < this.totalSlides - 1) {
          this.next();
          this.isDragging = false;
        } else if (difference < -this.swipeThreshold && this.currentIndex > 0) {
          this.prev();
          this.isDragging = false;
        }
      }
    }, { passive: false });
    
    this.container.addEventListener('touchend', () => {
      this.isDragging = false;
      this.isHorizontalSwipe = null;
      this.container.style.transition = this.transitionDuration;
    }, { passive: true });
    
    this.container.addEventListener('touchcancel', () => {
      this.isDragging = false;
      this.isHorizontalSwipe = null;
      this.container.style.transition = this.transitionDuration;
    }, { passive: true });
  }
  
  /**
   * Initialize conditional pagination (shows pagination only on mobile)
   * This method can be called manually or is called automatically on resize
   */
  initializeConditionalPagination() {
    if (window.innerWidth <= this.breakpoint) {
      // Mobile: create pagination if it doesn't exist, or update if it does
      if (this.paginationContainer) {
        if (this.paginationContainer.children.length === 0) {
          this.createPagination();
        } else {
          // If pagination already exists, ensure it's synced (first dot active)
          this.updatePagination();
        }
      }
    } else {
      // Desktop: clear pagination and counter
      if (this.paginationContainer) {
        this.paginationContainer.innerHTML = '';
      }
      if (this.slideCounter) {
        this.slideCounter.textContent = '';
      }
    }
  }
  
  /**
   * Handle window resize
   */
  handleResize() {
    if (window.innerWidth <= this.breakpoint) {
      this.updateCarousel();
      // Handle conditional pagination on mobile
      this.initializeConditionalPagination();
    } else {
      this.resetTransform();
      // Clear pagination on desktop
      this.initializeConditionalPagination();
    }
    this.updateButtonState(); // Update button state on resize
  }
  
  /**
   * Destroy carousel (cleanup)
   */
  destroy() {
    if (this.boundHandleResize) {
      window.removeEventListener('resize', this.boundHandleResize);
    }
    // Additional cleanup if needed
  }
}

// Export for use in other files
if (typeof module !== 'undefined' && module.exports) {
  module.exports = Carousel;
}
